import React from "react";

import { Badge, Box, ButtonBase, CircularProgress, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableRow, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';

import { v4 as uuid } from "uuid";

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // outline rounded doesn't exist?
import { useHistory, useLocation } from "react-router-dom";
import { ScoreGrade, scoreColor, sentenceScoreAllSilence } from "../Score";
import { AudioApi } from '../api/AudioApi';
import { CustomSentenceApi, ICustomSentence } from "../api/CustomSentenceApi";
import CustomSentenceListContext from '../context/CustomSentenceListContext';
import { ISentenceScore } from "../api/ScoreApi";
import DeleteBlueIcon from '../asset/delete-blue.png';
import CheckboxBlackIcon from '../asset/icon-check-black.png';
import CheckboxWhiteIcon from '../asset/icon-check-white.png';
import ShareIcon from '../asset/icon-share-black.png';
import SpeakerBlueIcon from '../asset/icon-speaker-blue.svg';
import iconLeaderboard from '../asset/leaderboard-icon-black.png';
import PlayBlackIcon from '../asset/play-black.png';
import PlayWhiteIcon from '../asset/play-white.png';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';
import { useAudioPlayer } from "../context/AudioPlayerContext";
import ShareDialog from "./ShareDialog";
import { BLUE, BORDER, SOFT_BLUE } from "../style/verbaScholaPalette";

type CustomSentenceRecorderListProps = {
    audio: string | null;
    selectedSentence: ICustomSentence;
    isMobile: boolean;
    redesigned?: boolean;
};

export type CustomSentenceRecorderAudio = {
    key: string;       // always uuid for React list
    id?: number;       // backend id (undefined until saved/fetched)
    audio: string;
    sentenceScore: ISentenceScore | null;
    timeStamp: string;
};

export const RECORDING_LIST = 'list';
export const RECORDING_SCORE = 'score'

export default function CustomSentenceRecordingList({ audio, selectedSentence, isMobile, redesigned = false }: CustomSentenceRecorderListProps) {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const [recordingToggle, setRecordingToggle] = React.useState<string | null>(RECORDING_LIST);

    const [selectedRecording, setSelectedRecording] = React.useState<CustomSentenceRecorderAudio | null>(null);
    const activeSentenceIdRef = React.useRef(selectedSentence.id);
    const asyncRequestVersionRef = React.useRef(0);

    const { refreshCustomSentenceList } = React.useContext(CustomSentenceListContext);

    const scoreAudioRef = React.useRef<(recording: CustomSentenceRecorderAudio, id: number) => void>(() => {});

    const handleScoreClick = React.useCallback((recording: CustomSentenceRecorderAudio) => {
        if (recording.sentenceScore) {
            // If sentenceScore already exists, just set it as selected
            setSelectedRecording(recording);
            setRecordingToggle(RECORDING_SCORE);
        } else {
            // Otherwise, score the audio
            scoreAudioRef.current(recording, selectedSentence.id);
            setRecordingToggle(RECORDING_SCORE);
        }
    }, [selectedSentence.id]);

    const { recordingArray, isScoring, setRecordingArray, setScoring, deleteAudio, deleteAllAudio } = useRecordingList(audio, handleScoreClick, selectedSentence.id);

    const handleRecordingToggleClick = (event: React.MouseEvent<HTMLElement>, newToggle: string | null) => {
        if (newToggle !== null) {
            setRecordingToggle(newToggle);
        }
    };

    React.useEffect(() => {
        activeSentenceIdRef.current = selectedSentence.id;
        asyncRequestVersionRef.current += 1;
        deleteAllAudio();
        setScoring(false);
        setRecordingToggle(RECORDING_LIST)
        setSelectedRecording(null)
    }, [selectedSentence.id, deleteAllAudio, setScoring])

    const playAudioFromURL = React.useCallback(async (recording: CustomSentenceRecorderAudio) => {
        const response = await fetch(recording.audio);
        const blob = await response.blob();
        playAudio(blob); // from useAudioPlayer()
    }, [playAudio]);

    const fetchRecordings = React.useCallback(async () => {
        const sentenceIdAtStart = selectedSentence.id;
        const requestVersionAtStart = ++asyncRequestVersionRef.current;
        try {
            const response = await CustomSentenceApi.getSentenceScore(sentenceIdAtStart);
            if (response.status >= 300) {
                throw new Error('Failed to fetch recordings: ' + response.statusText);
            }

            const recordingList: CustomSentenceRecorderAudio[] = await Promise.all(
                response.data.map(async (sentenceScore: any) => {
                    const audioResponse = await AudioApi.getSentenceScoreAudio(sentenceScore.id);
                    const audioBlob = audioResponse.data;
                    const audioUrl = URL.createObjectURL(audioBlob);

                    const date = new Date(sentenceScore.created_at);
                    const formatted =
                        date.getFullYear() +
                        '-' +
                        (date.getMonth() + 1).toString().padStart(2, '0') +
                        '-' +
                        date.getDate().toString().padStart(2, '0') +
                        ' ' +
                        date.getHours().toString().padStart(2, '0') +
                        ':' +
                        date.getMinutes().toString().padStart(2, '0') +
                        ':' +
                        date.getSeconds().toString().padStart(2, '0');

                    return {
                        key: uuid(),
                        id: sentenceScore.id,
                        audio: audioUrl,
                        sentenceScore: sentenceScore,
                        timeStamp: formatted,
                    };
                })
            );

            if (
                activeSentenceIdRef.current !== sentenceIdAtStart ||
                asyncRequestVersionRef.current !== requestVersionAtStart
            ) {
                return;
            }
            setRecordingArray(recordingList.reverse());
        } catch (err) {
            if (
                activeSentenceIdRef.current !== sentenceIdAtStart ||
                asyncRequestVersionRef.current !== requestVersionAtStart
            ) {
                return;
            }
            handleErrorMessage(err, setErrorMessage, history, location);
        }
    }, [selectedSentence.id, history, location, setErrorMessage, setRecordingArray]);

    React.useEffect(() => {
        fetchRecordings();
    }, [fetchRecordings]);

    const scoreAudio = React.useCallback(async (recording: CustomSentenceRecorderAudio, id: number) => {
        const sentenceIdAtStart = id;
        const requestVersionAtStart = ++asyncRequestVersionRef.current;
        setScoring(true);

        try {
            const response = await fetch(recording.audio);
            const blob = await response.blob();

            const formData = new FormData();
            formData.append("audio", blob);

            const scoreResponse = await CustomSentenceApi.scoreCustomSentence(id, formData);
            if (scoreResponse.status >= 300) {
                throw new Error("Scoring audio was not ok statuscode=" + scoreResponse.status + " " + scoreResponse.statusText);
            }

            const responseJson = scoreResponse.data;

            if (
                activeSentenceIdRef.current !== sentenceIdAtStart ||
                asyncRequestVersionRef.current !== requestVersionAtStart
            ) {
                return;
            }

            const updatedRecording = { ...recording, sentenceScore: responseJson };
            setSelectedRecording(updatedRecording);
            setRecordingArray(prev =>
                prev.map(r => (r.key === recording.key ? updatedRecording : r))
            );

            // Clear the spinner before the follow-up refresh so that
            // fetchRecordings() incrementing asyncRequestVersionRef does not
            // prevent the finally block from clearing it.
            setScoring(false);

            // Refresh from backend
            await fetchRecordings();
            refreshCustomSentenceList();
        } catch (err) {
            if (
                activeSentenceIdRef.current !== sentenceIdAtStart ||
                asyncRequestVersionRef.current !== requestVersionAtStart
            ) {
                return;
            }
            handleErrorMessage(err, setErrorMessage, history, location);
            setScoring(false);
        }
    }, [asyncRequestVersionRef, activeSentenceIdRef, fetchRecordings, refreshCustomSentenceList, setErrorMessage, history, location, setRecordingArray, setScoring]);

    scoreAudioRef.current = scoreAudio;

    React.useEffect(() => {
        if (selectedRecording) {
            playAudioFromURL(selectedRecording)
        }
    }, [selectedRecording, playAudioFromURL])

    const [isShareDialogOpen, setIsShareDialogOpen] = React.useState(false);

    const handleOpenShareDialog = () => {
        setIsShareDialogOpen(true);
    };

    return (
        <Stack alignItems="center">
            {/* Encapsulate horizontal alignment */}
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', // Adjusted for clarity
                alignItems: 'center', width: '100%'
            }}>
                {/* Invisible spacer to balance the layout */}
                {!isMobile && (
                    <Box sx={{ flex: 1 }}></Box>
                )}

                {/* For centering, we wrap the ToggleButtonGroup in a Box that takes up the needed space but doesn't affect its centering */}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                    <ToggleButtonGroup
                        value={recordingToggle}
                        exclusive
                        onChange={handleRecordingToggleClick}
                        aria-label="Recording details"
                        sx={{ pt: 2, pl: 1 }} // Adds padding to the top
                    >
                        <ToggleButton value={RECORDING_LIST} aria-label="Recordings" sx={{ minWidth: isMobile ? '130px' : '192px' }}>
                            {recordingToggle == RECORDING_LIST ? (
                                <img src={PlayWhiteIcon} style={{ width: '24px', height: '24px', marginRight: '16px' }} />
                            ) : (
                                <img src={PlayBlackIcon} style={{ width: '24px', height: '24px', marginRight: '16px' }} />
                            )}
                            {!isMobile && ("Your")} Recordings
                        </ToggleButton>
                        <ToggleButton value={RECORDING_SCORE} aria-label="Score" sx={{ minWidth: isMobile ? '130px' : '192px' }}>
                            {recordingToggle == RECORDING_SCORE ? (
                                <img src={CheckboxWhiteIcon} style={{ width: '24px', height: '24px', marginRight: '16px' }} />
                            ) : (
                                <img src={CheckboxBlackIcon} style={{ width: '24px', height: '24px', marginRight: '16px' }} />
                            )}
                            {!isMobile && ("Your")} Score
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                {/* Right Spacer: Specifically for "Share with Friends" to ensure it aligns to the right */}
                {!isMobile && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flex: 1
                    }}>
                        {selectedRecording && selectedRecording.sentenceScore && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                marginRight: '24px',
                                pt: 2,
                            }}>
                                <Tooltip title="Share with friends" arrow>
                                    <IconButton onClick={() => selectedRecording && selectedRecording.sentenceScore && handleOpenShareDialog()}>
                                        <img src={ShareIcon} alt="Share" style={{ width: '24px', height: '24px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {selectedRecording && selectedRecording.sentenceScore && (
                            <ShareDialog
                                isOpen={isShareDialogOpen}
                                onClose={() => setIsShareDialogOpen(false)}
                                sentenceScoreId={selectedRecording.sentenceScore.id} />
                        )}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginRight: '24px',
                            pt: 2,
                        }}>
                            <Tooltip title="Sentence Leaderboard" arrow>
                                <IconButton onClick={() => { history.push(`/leaderboard_sentence/${selectedSentence.id}`); }}>
                                    <img src={iconLeaderboard} alt="Leaderboard" style={{ width: '24px', height: '24px' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                )}
            </Box>
            {recordingToggle == RECORDING_LIST ? (
                <Box
                    sx={{
                        display: 'flex', // Use flex to center the child Box
                        flexDirection: 'column', // Stack child elements vertically
                        justifyContent: 'flex-start', // Center horizontally
                        alignItems: 'center', // Center vertically
                        height: 'calc(100vh - 550px)', // Parent container takes full viewport height
                        width: '75%', // Parent container takes full viewport width
                        backgroundColor: redesigned ? SOFT_BLUE : '#F2F7FF', // Set the background color
                        borderRadius: '16px', // Set the border radius
                        mt: 2, // Adds padding to the top
                        overflowY: 'auto', // Enables vertical scrolling if content overflows
                    }}
                >
                    {/* Reverse the array to display rows in reverse order */}
                    {[...recordingArray].map((recording, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'start',
                                mt: 2, // Margin top for spacing between rows
                                width: '80%'
                            }}
                        >
                            {/* <audio ref={(el: HTMLAudioElement | null) => (audioRefs.current[index] = el)} src={record.audio} /> */}
                            <IconButton aria-label={`Play recording from ${recording.timeStamp}`} onClick={() => playAudioFromURL(recording)} sx={{ minWidth: redesigned ? 44 : undefined, minHeight: redesigned ? 44 : undefined }}>
                                <img src={SpeakerBlueIcon} alt="Play" style={{ maxWidth: '24px', maxHeight: '24px' }} />
                            </IconButton>

                            <ButtonBase onClick={() => handleScoreClick(recording)} sx={{ flexGrow: 1, minHeight: redesigned ? 44 : undefined, mx: 2, justifyContent: 'flex-start', textAlign: 'left' }}>
                                <Typography>{recording.timeStamp} - {selectedSentence.text}</Typography>
                            </ButtonBase>

                            {/* Show the score if available */}
                            {(recording.sentenceScore && sentenceScoreAllSilence(recording.sentenceScore) == false) && (
                                <Badge
                                    badgeContent={Math.round(recording.sentenceScore.score_mean)}
                                    color="secondary"
                                    sx={{
                                        ml: 2,
                                        mr: 2,
                                        '.MuiBadge-badge': {
                                            backgroundColor: `${scoreColor(recording.sentenceScore.passing_grade)}22`,
                                            fontSize: '1rem',
                                            height: 24,
                                            minWidth: 24,
                                            borderRadius: '50%',
                                        }
                                    }}
                                >
                                    <Box sx={{ width: 0, height: 0 }} /> {/* placeholder */}
                                </Badge>
                            )}

                            {/* Checkbox shows tick only for selected recording */}
                            <IconButton aria-label={`View score for recording from ${recording.timeStamp}`} onClick={() => handleScoreClick(recording)} sx={{ ml: 2, minWidth: redesigned ? 44 : undefined, minHeight: redesigned ? 44 : undefined }}>
                                {selectedRecording && selectedRecording.key === recording.key ? (
                                    <img src={CheckboxBlackIcon} alt="Not selected" style={{ maxWidth: '24px', maxHeight: '24px' }} />
                                ) : (
                                    <img src={CheckboxWhiteIcon} alt="Selected" style={{ maxWidth: '24px', maxHeight: '24px' }} />
                                )}
                            </IconButton>

                            <IconButton aria-label={`Delete recording from ${recording.timeStamp}`} onClick={() => deleteAudio(recording)} disabled={!recording.id} sx={{ ml: 2, minWidth: redesigned ? 44 : undefined, minHeight: redesigned ? 44 : undefined }}>
                                <img src={DeleteBlueIcon} alt="Delete" style={{ maxWidth: '24px', maxHeight: '24px' }} />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            ) : (isScoring ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pt: 4 }}>
                    <CircularProgress />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Calculating your score
                    </Typography>
                </Box>
            ) : isMobile ? (
                <>
                    <ScoringHeader selectedRecording={selectedRecording} selectedSentence={selectedSentence} />
                    <TableContainer sx={{ p: 1 }}>
                        <Table sx={{}}>
                            <TableBody>
                                <TableRow>
                                    <TableCell align="center">
                                        <Tooltip title="Share with friends" arrow>
                                            <IconButton onClick={() => selectedRecording && selectedRecording.sentenceScore && handleOpenShareDialog()}>
                                                <img src={ShareIcon} alt="Share" style={{ width: '24px', height: '24px' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Sentence Leaderboard" arrow>
                                            <IconButton onClick={() => { history.push(`/leaderboard_sentence/${selectedSentence.id}`); }}>
                                                <img src={iconLeaderboard} alt="Leaderboard" style={{ width: '24px', height: '24px' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        {/* The audio element */}
                                        {selectedRecording && (<>
                                            {/* <audio ref={selectedAudioRef} src={selectedRecording.audio} /> */}
                                            <IconButton onClick={() => playAudioFromURL(selectedRecording)} sx={{ p: 0 }}>
                                                <img src={SpeakerBlueIcon} alt="Play" style={{ maxWidth: '24px', maxHeight: '24px' }} />
                                            </IconButton>
                                        </>)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {(selectedRecording != null && selectedRecording.sentenceScore && sentenceScoreAllSilence(selectedRecording.sentenceScore) == false) && (
                                            <Badge badgeContent={Math.round(selectedRecording.sentenceScore.score_mean)} color="secondary"
                                                sx={{
                                                    '.MuiBadge-badge': {
                                                        backgroundColor: `${scoreColor(selectedRecording.sentenceScore ? selectedRecording.sentenceScore.passing_grade : ScoreGrade.Good)}22`,
                                                        fontSize: '1rem', // <-- Adjust font size here
                                                        fontWeight: 'bold', // optional
                                                        height: 32, // optional: increase badge size
                                                        minWidth: 32,
                                                        borderRadius: '50%', // keeps it circular if you increase size     
                                                        // top: 16
                                                    },
                                                }}>
                                                <Box sx={{ width: 0, height: 0 }} /> {/* Placeholder for alignment */}
                                            </Badge>
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="center" component="th" scope="row" sx={{ py: 1 }}>
                                        Word
                                    </TableCell>
                                    <TableCell align="center" component="th" scope="row" sx={{ py: 1 }}>
                                        Correct
                                    </TableCell>
                                    <TableCell align="center" component="th" scope="row" sx={{ py: 1 }}>
                                        You
                                    </TableCell>
                                    <TableCell align="center" component="th" scope="row" sx={{ py: 1 }}>
                                        Score
                                    </TableCell>
                                </TableRow>
                                {(selectedRecording != null && selectedRecording.sentenceScore != null) && <>
                                    {sentenceScoreAllSilence(selectedRecording.sentenceScore) ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                                                Sentence Incomplete
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        selectedRecording.sentenceScore.score.map((value, index) => (
                                            <TableRow key={index}>
                                                <TableCell align="center" sx={{ py: 1 }}>
                                                    {selectedRecording.sentenceScore && selectedRecording.sentenceScore.text[index]}
                                                </TableCell>
                                                <TableCell align="center" sx={{
                                                    py: 1,
                                                    backgroundColor: `${scoreColor(ScoreGrade.Good)}22`  // 22 is 13.33% opacity
                                                }}>
                                                    {selectedRecording.sentenceScore && selectedRecording.sentenceScore.pinyin_list_transcript[index]}
                                                </TableCell>
                                                <TableCell align="center" sx={{
                                                    py: 1,
                                                    backgroundColor: `${scoreColor(selectedRecording.sentenceScore ? selectedRecording.sentenceScore.score_grade[index] : ScoreGrade.Good)}22`  // 22 is 13.33% opacity
                                                }}>
                                                    {selectedRecording.sentenceScore && selectedRecording.sentenceScore.pinyin_list_spoken[index]}
                                                </TableCell>
                                                <TableCell align="center" sx={{
                                                    py: 1,
                                                    backgroundColor: `${scoreColor(selectedRecording.sentenceScore ? selectedRecording.sentenceScore.score_grade[index] : ScoreGrade.Good)}22`  // 22 is 13.33% opacity
                                                }}>

                                                    {selectedRecording.sentenceScore && Math.round(selectedRecording.sentenceScore.score[index])}
                                                </TableCell>
                                            </TableRow>
                                        )))}
                                </>}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            ) : (
                <>
                    <ScoringHeader selectedRecording={selectedRecording} selectedSentence={selectedSentence} />
                    <TableContainer sx={{ overflowX: 'auto', p: 2 }}>
                        <Table aria-label="simple table" sx={{ minWidth: 650, border: 1, borderColor: 'rgba(224, 224, 224, 1)', margin: 'auto', width: 'auto' }}>
                            <TableBody>
                                <TableRow>
                                    <TableCell component="th" scope="row" sx={{ py: 1, borderTopLeftRadius: '16px', maxWidth: '120px' }}>
                                        Word
                                    </TableCell>
                                    {(selectedRecording != null && selectedRecording.sentenceScore != null) && <>
                                        {selectedRecording.sentenceScore.score.map((value, index) =>
                                            <TableCell align="center" sx={{ py: 1 }}>
                                                {selectedRecording.sentenceScore && selectedRecording.sentenceScore.text[index]}
                                            </TableCell>
                                        )}
                                    </>}
                                </TableRow>

                                <TableRow>
                                    <TableCell component="th" scope="row" sx={{ py: 1, maxWidth: '120px' }}>
                                        Correct Pronunciation
                                    </TableCell>
                                    {(selectedRecording != null && selectedRecording.sentenceScore != null) && <>
                                        {selectedRecording.sentenceScore.score.map((value, index) =>
                                            <TableCell align="center" sx={{
                                                py: 1,
                                                backgroundColor: `${scoreColor(ScoreGrade.Good)}22`  // 22 is 13.33% opacity
                                            }}>
                                                {selectedRecording.sentenceScore && selectedRecording.sentenceScore.pinyin_list_transcript[index]}
                                            </TableCell>
                                        )}
                                    </>}
                                </TableRow>

                                <TableRow>
                                    <TableCell component="th" scope="row" sx={{
                                        py: 1,
                                        alignItems: 'center', // Vertically center the content
                                        gap: '8px', // Optional: Add space between the text and the image
                                        maxWidth: '120px'
                                    }}>
                                        Your Pronunciation
                                        {/* The audio element */}
                                        {selectedRecording && <>
                                            {/* <audio ref={selectedAudioRef} src={selectedRecording.audio} /> */}
                                            <IconButton onClick={() => playAudioFromURL(selectedRecording)} sx={{ p: 0, ml: 2 }}>
                                                <img src={SpeakerBlueIcon} alt="Play" style={{ maxWidth: '24px', maxHeight: '24px' }} />
                                            </IconButton>
                                        </>}
                                    </TableCell>
                                    {(selectedRecording != null && selectedRecording.sentenceScore != null) && <>
                                        {sentenceScoreAllSilence(selectedRecording.sentenceScore) ? (
                                            <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                                                No Speech Detected
                                            </TableCell>
                                        ) : (
                                            selectedRecording.sentenceScore.score.map((value, index) =>
                                                <TableCell align="center" sx={{
                                                    py: 1,
                                                    backgroundColor: `${scoreColor(selectedRecording.sentenceScore ? selectedRecording.sentenceScore.score_grade[index] : ScoreGrade.Good)}22`  // 22 is 13.33% opacity
                                                }}>
                                                    {selectedRecording.sentenceScore && selectedRecording.sentenceScore.pinyin_list_spoken[index]}
                                                </TableCell>
                                            )
                                        )}</>}
                                </TableRow>

                                <TableRow>
                                    <TableCell component="th" scope="row" sx={{ py: 1, borderBottomLeftRadius: '16px', maxWidth: '120px' }}>
                                        Total Score
                                        {(selectedRecording != null && selectedRecording.sentenceScore && sentenceScoreAllSilence(selectedRecording.sentenceScore) == false) &&
                                            <Badge badgeContent={Math.round(selectedRecording.sentenceScore.score_mean)} color="secondary"
                                                sx={{
                                                    '.MuiBadge-badge': {
                                                        backgroundColor: `${scoreColor(selectedRecording.sentenceScore ? selectedRecording.sentenceScore.passing_grade : ScoreGrade.Good)}22`,
                                                        fontSize: '1rem', // <-- Adjust font size here
                                                        fontWeight: 'bold', // optional
                                                        height: 32, // optional: increase badge size
                                                        minWidth: 32,
                                                        borderRadius: '50%', // keeps it circular if you increase size     

                                                    }, ml: 4,
                                                }}>
                                                {/* Empty div as a placeholder */}
                                                <div></div>
                                            </Badge>

                                        }
                                    </TableCell>
                                    {(selectedRecording != null && selectedRecording.sentenceScore != null && sentenceScoreAllSilence(selectedRecording.sentenceScore) == false) && <>
                                        {selectedRecording.sentenceScore.score.map((value, index) =>
                                            <TableCell align="center" sx={{
                                                py: 1,
                                                backgroundColor: `${scoreColor(selectedRecording.sentenceScore ? selectedRecording.sentenceScore.score_grade[index] : ScoreGrade.Good)}22`  // 22 is 13.33% opacity
                                            }}>
                                                {selectedRecording.sentenceScore && Math.round(selectedRecording.sentenceScore.score[index])}
                                            </TableCell>
                                        )}
                                    </>}
                                </TableRow>
                                {(selectedRecording != null && selectedRecording.sentenceScore != null) && <>
                                    <TableRow>
                                        <TableCell
                                            colSpan={selectedRecording.sentenceScore.score.length + 1}
                                            sx={{ backgroundColor: redesigned ? SOFT_BLUE : '#F2F7FFCC', borderTop: `1px solid ${redesigned ? BORDER : '#ccc'}`, py: 2 }}
                                        >
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                                <InfoOutlinedIcon sx={{ fontSize: 24, color: redesigned ? BLUE : '#0062FF', mr: 1 }} />
                                                <Box component="span" sx={{ fontWeight: 'bold' }}>1.</Box> Compare your audio to the reference audio.
                                                <Box component="span" sx={{ fontWeight: 'bold', ml: 2 }}>2.</Box> Compare your pinyin to the reference pinyin.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>

                                </>}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            ))}
        </Stack>
    );
}

export function useRecordingList(audio: string | null, handleScoreClick: (record: CustomSentenceRecorderAudio) => void, sentenceId: number) {
    const [recordingArray, setRecordingArray] = React.useState<CustomSentenceRecorderAudio[]>([]);
    const [isScoring, setScoring] = React.useState(false);
    const { refreshCustomSentenceList } = React.useContext(CustomSentenceListContext);

    React.useEffect(() => {
        setScoring(false);
    }, [sentenceId]);

    function generateTimestamp(): string {
        const now = new Date();
        return (
            now.getFullYear() + '-' +
            (now.getMonth() + 1).toString().padStart(2, '0') + '-' +
            now.getDate().toString().padStart(2, '0') + ' ' +
            now.getHours().toString().padStart(2, '0') + ':' +
            now.getMinutes().toString().padStart(2, '0') + ':' +
            now.getSeconds().toString().padStart(2, '0')
        );
    }

    React.useEffect(() => {
        if (audio) {
            const nextRecorderAudio: CustomSentenceRecorderAudio = {
                key: uuid(), // frontend key for React rendering
                audio,
                sentenceScore: null,
                timeStamp: generateTimestamp(),
            };
            setRecordingArray(prev => [nextRecorderAudio, ...prev]);
            handleScoreClick(nextRecorderAudio);
        }
    }, [audio, handleScoreClick]);

    const deleteAudio = React.useCallback(async (record: CustomSentenceRecorderAudio) => {
        // remove from UI first
        setRecordingArray(prev => prev.filter(r => r.key !== record.key));

        // If there's no backend ID yet, just remove it locally
        if (!record.id) {
            return;
        }

        try {
            await CustomSentenceApi.deleteSentenceScore(sentenceId, record.id);
            refreshCustomSentenceList();
        } catch (err) {
            console.error("Failed to delete recording:", err);
        }
    }, [sentenceId, refreshCustomSentenceList]);

    const deleteAllAudio = React.useCallback(() => {
        setRecordingArray([]);
        setScoring(false);
    }, []);

    return {
        recordingArray,
        isScoring,
        setRecordingArray,
        setScoring,
        deleteAudio,
        deleteAllAudio,
    };
}

function ScoringHeader({ selectedRecording, selectedSentence }: {
    selectedRecording: CustomSentenceRecorderAudio | null,
    selectedSentence: ICustomSentence
}) {
    if (!selectedRecording) return null;

    return (
        <Box display="flex" alignItems="center" m={2}>
            <Typography variant="body1" sx={{ mr: 2, fontWeight: 'bold' }}>
                Scoring this recording:
            </Typography>
            <Typography variant="body2" sx={{ mr: 2 }}>
                {selectedRecording.timeStamp} – {selectedSentence.text}
            </Typography>
        </Box>
    );
}
